import { Component } from '@theme/component';
import { closest, clamp, center, getVisibleElements } from '@theme/utilities';
import { Scroller } from '@theme/scrolling';
import { cycleFocus } from '@theme/focus';

/**
 * Collection pills navigation component
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} [pills]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLElement} [dotsContainer]
 *
 * @extends {Component<Refs>}
 */
class CollectionPills extends Component {
  requiredRefs = ['container'];

  /** @type {Scroller} */
  #scroll;

  /** @type {ResizeObserver} */
  #resizeObserver;

  /** Bound resize handler for cleanup */
  #boundHandleResize = this.#handleResize.bind(this);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);

    this.#scroll = new Scroller(this.refs.container, {
      onScroll: this.#handleScroll,
    });

    // Set up resize observer to handle layout changes
    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateDotsVisibility();
    });
    this.#resizeObserver.observe(this.refs.container);

    // IMPORTANT: Calculate page count FIRST before initializing current pill
    // Wait for layout to settle, then calculate dots visibility and initialize
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.#updateDotsVisibility(); // Calculate #pageCount first
        this.#initializeCurrentPill(); // Then initialize with correct pageCount
      }, 100);
    });

    // Also check on window resize
    window.addEventListener('resize', this.#boundHandleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scroll?.destroy();
    this.#resizeObserver?.disconnect();
    window.removeEventListener('resize', this.#boundHandleResize);
  }

  /**
   * Handle window resize
   */
  #handleResize() {
    this.#updateDotsVisibility();
  }

  get pills() {
    return this.refs.pills || [];
  }

  get dots() {
    return this.refs.dots || [];
  }

  get currentIndex() {
    return this.pills.findIndex((pill) => pill.getAttribute('aria-current') === 'true');
  }

  /**
   * Initialize the current pill and scroll it into view
   */
  #initializeCurrentPill() {
    const currentIndex = this.currentIndex;
    if (currentIndex >= 0) {
      const currentPill = this.pills[currentIndex];
      if (currentPill) {
        // Scroll to the current pill without animation on load
        this.#scroll.to(currentPill, { instant: true });

        // Calculate which page/dot this pill belongs to
        const { container } = this.refs;
        const containerWidth = container.clientWidth;
        const totalWidth = container.scrollWidth;
        const avgPillWidth = totalWidth / this.pills.length;
        const pillsPerPage = Math.max(1, Math.floor(containerWidth / avgPillWidth));

        // Calculate the page index for this pill
        const pageIndex = Math.floor(currentIndex / pillsPerPage);

        // Update dots immediately with the correct page
        requestAnimationFrame(() => {
          this.#updateDots(pageIndex);
        });
      }
    }
  }

  /** @type {number} Current page count for dots */
  #pageCount = 1;

  /**
   * Select a page by index (triggered by dot click)
   *
   * @param {number} pageIndex
   * @param {Event} [event]
   */
  selectDot(pageIndex, event) {
    event?.preventDefault();

    const { container } = this.refs;
    const { pills } = this;
    if (!pills.length) return;

    // Calculate which pill to scroll to based on page index
    const containerWidth = container.clientWidth;
    const totalWidth = container.scrollWidth;
    const avgPillWidth = totalWidth / pills.length;
    const pillsPerPage = Math.max(1, Math.floor(containerWidth / avgPillWidth));

    // Get the first pill of the target page
    const targetPillIndex = Math.min(pageIndex * pillsPerPage, pills.length - 1);
    const targetPill = pills[targetPillIndex];

    if (targetPill) {
      this.#scroll.to(targetPill);
      this.#updateDots(pageIndex);
    }
  }

  /**
   * Update the selected dot based on page index
   *
   * @param {number} pageIndex
   */
  #updateDots(pageIndex) {
    const { dots } = this;
    if (!dots.length) return;

    const selectedIndex = clamp(pageIndex, 0, this.#pageCount - 1);

    for (const [i, dot] of dots.entries()) {
      dot.setAttribute('aria-selected', Boolean(i === selectedIndex).toString());
    }
  }

  /**
   * Handle scroll event to sync dots with current page
   */
  #handleScroll = () => {
    const { container } = this.refs;
    const { pills } = this;

    if (!pills.length || this.#pageCount <= 1) return;

    // Calculate current page based on scroll position
    const scrollLeft = container.scrollLeft;
    const containerWidth = container.clientWidth;
    const maxScroll = container.scrollWidth - containerWidth;

    if (maxScroll <= 0) {
      this.#updateDots(0);
      return;
    }

    // Calculate page index based on scroll position
    const scrollProgress = scrollLeft / maxScroll;
    const currentPage = Math.round(scrollProgress * (this.#pageCount - 1));

    this.#updateDots(currentPage);
  };

  /**
   * Handle keyboard navigation
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    let modifier = 0;

    switch (event.key) {
      case 'ArrowRight':
        modifier = 1;
        break;
      case 'ArrowLeft':
        modifier = -1;
        break;
    }

    if (!modifier) return;

    event.preventDefault();
    cycleFocus(this.pills, modifier);

    // Also scroll to the focused pill
    const focusedIndex = this.pills.findIndex((pill) => pill === document.activeElement);
    if (focusedIndex >= 0) {
      const focusedPill = this.pills[focusedIndex];
      if (focusedPill) {
        this.#scroll.to(focusedPill);
        this.#updateDots(focusedIndex);
      }
    }
  }

  /**
   * Update dots visibility and page count based on scrollability
   */
  #updateDotsVisibility() {
    const { dotsContainer, container } = this.refs;
    const { pills, dots } = this;

    if (!dotsContainer || !pills.length) {
      this.#pageCount = 1;
      return;
    }

    // Only show dots on mobile (< 750px)
    const isMobile = window.innerWidth < 750;
    if (!isMobile) {
      dotsContainer.setAttribute('data-visible', 'false');
      this.#pageCount = 1;
      return;
    }

    const containerWidth = container.clientWidth;
    const totalWidth = container.scrollWidth;

    // Check if scrollable (with small tolerance)
    if (totalWidth <= containerWidth + 10) {
      dotsContainer.setAttribute('data-visible', 'false');
      this.#pageCount = 1;
      return;
    }

    // Calculate number of "pages" based on how many pills fit per viewport
    const avgPillWidth = totalWidth / pills.length;
    const pillsPerPage = Math.max(1, Math.floor(containerWidth / avgPillWidth));
    const pageCount = Math.ceil(pills.length / pillsPerPage);

    this.#pageCount = pageCount;

    // Show/hide dots based on page count
    dots.forEach((dot, i) => {
      dot.style.display = i < pageCount ? '' : 'none';
    });

    const showDots = this.hasAttribute('show-dots');

    if (showDots && pageCount > 1) {
      dotsContainer.setAttribute('data-visible', 'true');
    } else {
      dotsContainer.setAttribute('data-visible', 'false');
    }
  }
}

if (!customElements.get('collection-pills-component')) {
  customElements.define('collection-pills-component', CollectionPills);
}
